/**
 * Supported format tokens:
 *
 *   yyyy  — 4-digit year            (e.g. 1990)
 *   yy    — 2-digit year            (e.g. 90)
 *   MM    — 2-digit month  01-12    (e.g. 03)
 *   M     — month without padding   (e.g. 3)
 *   dd    — 2-digit day    01-31    (e.g. 05)
 *   d     — day without padding     (e.g. 5)
 *
 * Common patterns:
 *   'dd/MM/yyyy'  → 15/03/1990
 *   'yyyy/MM/dd'  → 1990/03/15
 *   'MM/dd/yyyy'  → 03/15/1990
 *   'dd.MM.yyyy'  → 15.03.1990
 *   'yyyy-MM-dd'  → 1990-03-15
 */

export const MS_PER_DAY = 1_000 * 60 * 60 * 24;

/** Display date pattern for French (and the app default) — pass to {@link formatDate}. */
export const DATE_FORMAT = 'dd/MM/yyyy';

/** Display date pattern for English (US month-first order). */
export const DATE_FORMAT_EN = 'MM/dd/yyyy';

/**
 * Picks the display date pattern for a language code: English → `MM/dd/yyyy`
 * (US order), everything else → `dd/MM/yyyy` (French/default). Accepts region
 * variants (`en`, `en-US`, …). Pass the result to {@link formatDate}.
 */
export function dateFormatForLang(lng: string | undefined): string {
  return lng?.toLowerCase().startsWith('en') ? DATE_FORMAT_EN : DATE_FORMAT;
}

/** Regex matching the leading date part of any ISO 8601 string. */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Returns whether a date string is expired and how many days ago it expired. */
export function getExpirationInfo(isoDate: string, now = Date.now()): { isExpired: boolean; daysAgo: number } {
  const ms = new Date(isoDate).getTime();
  const isExpired = ms < now;
  const daysAgo = Math.floor((now - ms) / MS_PER_DAY);
  return { isExpired, daysAgo };
}

export function getDaysLeft(expiresAt: string, now = Date.now()): number {
  return Math.ceil((new Date(expiresAt).getTime() - now) / MS_PER_DAY);
}

/**
 * Formats an ISO date string into any pattern without UTC offset side-effects.
 *
 * Why not `new Date()`?
 * `new Date('1990-03-15')` is parsed as UTC midnight. In any timezone behind
 * UTC the local date becomes March 14 — the wrong day. This function reads the
 * date components directly from the string so the result is always the
 * calendar date that appears in the source data.
 *
 * @param value  ISO date string: `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm:ss…`
 * @param format Format pattern built from the supported tokens listed above.
 * @returns      Formatted string, or the original `value` when it cannot be parsed.
 */
export function formatDate(value: string, format: string): string {
  const match = ISO_DATE_RE.exec(value);
  if (!match) return value;

  const [, yyyy, MM, dd] = match;

  // Build a lookup for every token.  Longer tokens must appear before their
  // shorter prefixes in the regex alternation to prevent partial matches
  // (e.g. 'MM' must match before 'M').
  const tokens: Record<string, string> = {
    yyyy,
    yy: yyyy.slice(-2),
    MM,
    M: String(Number(MM)),
    dd,
    d: String(Number(dd)),
  };

  return format.replace(/yyyy|yy|MM|M|dd|d/g, token => tokens[token]);
}
